import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useLocalStorage } from "@rehooks/local-storage";
import { useMemo } from "react";

import usePaginationCursorReset from "components/Table/Pagination/usePaginationCursorReset";
import { SearchTofuWorkspacesOutput } from "types/generated";
import {
  STACK_WORKSPACES_LIMIT,
  STACK_WORKSPACES_LIMIT_STORAGE_KEY,
} from "views/Stack/Workspaces/Table/constants";

import useTofuWorkspacesFiltersVariables from "../useSearchTofuWorkspacesSuggestions/useFiltersVariables";
import { SEARCH_TOFU_WORKSPACES } from "./gql";
import useTofuWorkspacesStore from "./store";

type UseSearchTofuWorkspacesProps = {
  stackId: string;
};

const useSearchTofuWorkspaces = ({ stackId }: UseSearchTofuWorkspacesProps) => {
  const {
    cursor: storedCursor,
    requestedPage: storedRequestedPage,
    resetPagination,
  } = useTofuWorkspacesStore();
  const [storedRowsPerPage] = useLocalStorage<number>(STACK_WORKSPACES_LIMIT_STORAGE_KEY);
  const { sortOptionFields, fullTextSearch } = useTofuWorkspacesFiltersVariables();

  const first = storedRowsPerPage || STACK_WORKSPACES_LIMIT;

  const paginationResetKey = useMemo(
    () => ({ stackId, fullTextSearch, sortOptionFields }),
    [stackId, fullTextSearch, sortOptionFields]
  );

  const { cursor, requestedPage } = usePaginationCursorReset({
    resetKey: paginationResetKey,
    cursor: storedCursor,
    requestedPage: storedRequestedPage,
    resetPagination,
  });

  const { data, error, networkStatus, previousData } = useQuery<{
    searchTofuWorkspaces: SearchTofuWorkspacesOutput;
  }>(SEARCH_TOFU_WORKSPACES, {
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        first,
        after: cursor,
        requestedPage,
        orderBy: sortOptionFields,
        fullTextSearch,
        predicates: [
          {
            field: "stack",
            constraint: {
              stringEquals: [stackId],
            },
          },
        ],
      },
    },
  });

  const workspaces = useMemo(() => {
    const edges =
      data?.searchTofuWorkspaces.edges ?? previousData?.searchTofuWorkspaces.edges ?? [];

    return edges.map((edge) => edge.node);
  }, [data, previousData]);

  const initialLoading = workspaces.length === 0 && networkStatus === NetworkStatus.loading;
  const loadingContent =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.fetchMore ||
    networkStatus === NetworkStatus.setVariables;
  const hasSearchQuery = !!fullTextSearch;

  const hasLoadedPage = Boolean(data);
  const isEmpty = hasLoadedPage && !workspaces.length && !initialLoading && !hasSearchQuery;
  const searchIsEmpty = hasLoadedPage && !workspaces.length && !initialLoading && hasSearchQuery;

  return {
    workspaces,
    error,
    initialLoading,
    loadingContent,
    isEmpty,
    searchIsEmpty,
    startCursor: data?.searchTofuWorkspaces?.pageInfo.startCursor,
    endCursor: data?.searchTofuWorkspaces?.pageInfo.endCursor,
    paginationResetKey,
  };
};

export default useSearchTofuWorkspaces;
